// Nested objects cover MUI sx selectors (`button: {...}`, `&:hover: {...}`),
// which Chakra's `css` prop supports natively.
export type SxValue = string | number | Record<string, string | number>;
export type Sx = Record<string, SxValue>;

// MUI `m`/`p` shorthands → CSS longhands. Numeric values on spacing keys use
// MUI's 8px spacing scale (theme.spacing(1) === 8px); everything else is
// treated as literal CSS so the rendered pixels are identical to the old
// MUI-Box implementation regardless of Chakra's own token scale.
const SHORTHAND: Record<string, Array<string>> = {
  m: ['margin'],
  mt: ['marginTop'],
  mb: ['marginBottom'],
  ml: ['marginLeft'],
  mr: ['marginRight'],
  mx: ['marginLeft', 'marginRight'],
  my: ['marginTop', 'marginBottom'],
  p: ['padding'],
  pt: ['paddingTop'],
  pb: ['paddingBottom'],
  pl: ['paddingLeft'],
  pr: ['paddingRight'],
  px: ['paddingLeft', 'paddingRight'],
  py: ['paddingTop', 'paddingBottom'],
};

const SPACING_KEYS = new Set([
  'margin',
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',
  'padding',
  'paddingTop',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',
  'gap',
]);

/**
 * Translate a MUI `sx` object into a plain CSS object suitable for Chakra's
 * `css` prop: expand `m`/`p` shorthands and convert numeric spacing values
 * (MUI's 8px scale) into explicit `px` strings. Nested selector objects pass
 * through untouched (only top-level numeric spacing is scaled).
 */
export const sxToCss = (sx: Sx = {}): Sx => {
  const out: Sx = {};
  const put = (key: string, value: SxValue): void => {
    out[key] = SPACING_KEYS.has(key) && typeof value === 'number' ? `${value * 8}px` : value;
  };
  for (const [key, value] of Object.entries(sx)) {
    const longhands = SHORTHAND[key];
    if (longhands) {
      for (const longhand of longhands) {
        put(longhand, value);
      }
    } else {
      put(key, value);
    }
  }
  return out;
};
